import React, { useRef, useState } from "react";
import HUDFrame from "../components/HUDFrame";
import StrainScanLogo from "../components/StrainScanLogo";
import { Download, Copy, Check } from "lucide-react";

/**
 * /brand — press-kit page with downloadable assets.
 * Each variant is rendered live and can be exported to SVG (vector) or PNG (raster, multiple sizes).
 * Uses XMLSerializer + canvas to convert the live DOM SVG to files in-browser — no backend.
 */

const VARIANTS = [
  {
    key: "full",
    label: "Full Logo",
    desc: "Leaf in scanner + STRAINSCAN PRO wordmark. Use for hero, about, marketing.",
    bg: "transparent",
    pad: 64,
  },
  {
    key: "mark",
    label: "Icon / Mark",
    desc: "Leaf-in-scanner only. Use for app icon, favicon, social avatars.",
    bg: "transparent",
    pad: 32,
  },
  {
    key: "wordmark",
    label: "Wordmark",
    desc: "STRAINSCAN PRO type only. Use in narrow horizontal layouts.",
    bg: "transparent",
    pad: 32,
  },
];

const COLORS = [
  { name: "Neon Green", hex: "#9DFC04", role: "Primary brand" },
  { name: "Deep Black", hex: "#02060A", role: "Background" },
  { name: "Glow White", hex: "#F5FFF0", role: "Headlines" },
  { name: "Tech Purple", hex: "#9A4CFF", role: "Accent (Medical Care)" },
  { name: "Hot Pink", hex: "#FF3BB7", role: "Accent (Medical Care)" },
];

const FONTS = [
  { name: "Orbitron", role: "Display / Wordmark", weight: "700–900" },
  { name: "Rajdhani", role: "UI body / Subheads", weight: "500–700" },
  { name: "JetBrains Mono", role: "Numbers / Codes", weight: "500" },
];

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function serializeSvgFromContainer(container) {
  // If the container has a designated "export" SVG (used by the wordmark
  // variant which renders an HTML version + a hidden SVG twin), use it
  // directly with its native viewBox.
  const exportSvg = container.querySelector("svg[data-export]");
  if (exportSvg) {
    const clone = exportSvg.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.removeAttribute("style");
    clone.removeAttribute("data-export");
    return new XMLSerializer().serializeToString(clone);
  }

  const svgs = container.querySelectorAll("svg");
  if (svgs.length === 0) return null;

  // Compute the bounding box (in screen coords) so we can stack them.
  const containerRect = container.getBoundingClientRect();
  const viewW = Math.ceil(containerRect.width);
  const viewH = Math.ceil(containerRect.height);

  // Create a new root SVG that mirrors the layout of the container by
  // positioning each child SVG absolutely using its computed bounding box.
  const ns = "http://www.w3.org/2000/svg";
  const root = document.createElementNS(ns, "svg");
  root.setAttribute("xmlns", ns);
  root.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  root.setAttribute("viewBox", `0 0 ${viewW} ${viewH}`);
  root.setAttribute("width", viewW);
  root.setAttribute("height", viewH);

  svgs.forEach((svg) => {
    const r = svg.getBoundingClientRect();
    const x = r.left - containerRect.left;
    const y = r.top - containerRect.top;
    const w = r.width;
    const h = r.height;
    const clone = svg.cloneNode(true);
    // Outer positioning
    const wrapper = document.createElementNS(ns, "g");
    wrapper.setAttribute("transform", `translate(${x} ${y})`);
    clone.setAttribute("width", w);
    clone.setAttribute("height", h);
    wrapper.appendChild(clone);
    root.appendChild(wrapper);
  });

  // For wordmark / divs (HTML text), embed via foreignObject so SVG export
  // captures CSS text. Fallback: include rendered HTML as foreignObject.
  const htmlBlocks = container.querySelectorAll(":scope > div, :scope > div > div");
  htmlBlocks.forEach((div) => {
    if (div.querySelector("svg")) return; // skip wrappers around SVGs
    if (!div.innerText.trim()) return;
    const r = div.getBoundingClientRect();
    const fo = document.createElementNS(ns, "foreignObject");
    fo.setAttribute("x", r.left - containerRect.left);
    fo.setAttribute("y", r.top - containerRect.top);
    fo.setAttribute("width", r.width);
    fo.setAttribute("height", r.height);
    const wrap = document.createElement("div");
    wrap.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
    wrap.innerHTML = div.outerHTML;
    fo.appendChild(wrap);
    root.appendChild(fo);
  });

  return new XMLSerializer().serializeToString(root);
}

async function exportPng(svgString, width) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const ratio = img.height / img.width;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = Math.round(width * ratio);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "transparent";
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => {
        URL.revokeObjectURL(url);
        if (b) resolve(b);
        else reject(new Error("PNG conversion failed"));
      }, "image/png");
    };
    img.onerror = () => reject(new Error("SVG failed to render to image"));
    img.src = url;
  });
}

function VariantCard({ variant }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(null);

  const downloadSVG = () => {
    setBusy("svg");
    try {
      const svgStr = serializeSvgFromContainer(ref.current);
      if (!svgStr) return;
      const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${svgStr}`], {
        type: "image/svg+xml;charset=utf-8",
      });
      downloadBlob(blob, `strainscan-pro-${variant.key}.svg`);
    } finally {
      setBusy(null);
    }
  };

  const downloadPNG = async (size) => {
    setBusy(`png-${size}`);
    try {
      const svgStr = serializeSvgFromContainer(ref.current);
      if (!svgStr) return;
      const blob = await exportPng(svgStr, size);
      downloadBlob(blob, `strainscan-pro-${variant.key}-${size}.png`);
    } catch (e) {
      alert("Export failed: " + e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <HUDFrame padding="p-6 md:p-8" data-testid={`brand-card-${variant.key}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xl md:text-2xl font-display font-black text-white tracking-wide">
            {variant.label}
          </h3>
          <p className="mt-1 text-[color:var(--ss-muted)] text-sm max-w-md">
            {variant.desc}
          </p>
        </div>
      </div>

      {/* Live preview (also our serialization source) */}
      <div
        ref={ref}
        className="mt-6 grid place-items-center w-full"
        style={{
          background: variant.bg === "transparent"
            ? "repeating-conic-gradient(rgba(255,255,255,0.04) 0% 25%, transparent 0% 50%) 50% / 24px 24px"
            : variant.bg,
          padding: variant.pad,
          minHeight: 280,
          maxHeight: variant.key === "full" ? "none" : undefined,
          overflow: "visible",
          border: "1px dashed rgba(157,252,4,0.2)",
        }}
      >
        <StrainScanLogo variant={variant.key} animated={false} />
      </div>

      {/* Downloads */}
      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          className="btn-primary"
          onClick={downloadSVG}
          disabled={busy === "svg"}
          data-testid={`download-svg-${variant.key}`}
        >
          <Download size={14} /> {busy === "svg" ? "Exporting…" : "SVG"}
        </button>
        {[512, 1024, 2048].map((s) => (
          <button
            key={s}
            type="button"
            className="btn-ghost"
            onClick={() => downloadPNG(s)}
            disabled={busy === `png-${s}`}
            data-testid={`download-png-${variant.key}-${s}`}
          >
            <Download size={14} /> {busy === `png-${s}` ? "Exporting…" : `PNG ${s}px`}
          </button>
        ))}
      </div>
    </HUDFrame>
  );
}

function CopyableHex({ hex }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1300);
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="font-mono text-xs text-[color:var(--ss-green)] inline-flex items-center gap-1.5 hover:text-white transition"
    >
      {hex} {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

export default function Brand() {
  return (
    <div className="relative z-10 pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="hud-pill">PRESS KIT</div>
        <h1 className="mt-6 font-display font-black leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
          <span className="text-white">Brand assets &</span>
          <br />
          <span className="text-green glow">downloads.</span>
        </h1>
        <p className="mt-5 max-w-2xl text-[color:var(--ss-muted)] text-lg">
          Official StrainScan Pro logos, color palette, and typography. Click any
          format below to download — vector SVG or rasterized PNG at multiple
          resolutions.
        </p>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          {VARIANTS.map((v) => (
            <VariantCard key={v.key} variant={v} />
          ))}
        </div>

        {/* Color palette */}
        <div className="mt-16">
          <div className="hud-pill">COLOR PALETTE</div>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {COLORS.map((c) => (
              <HUDFrame key={c.hex} padding="p-4">
                <div
                  className="w-full h-24 mb-3 border border-[rgba(255,255,255,0.08)]"
                  style={{ background: c.hex }}
                />
                <div className="font-display font-bold text-white text-sm">{c.name}</div>
                <div className="text-xs text-[color:var(--ss-muted)] mb-2">{c.role}</div>
                <CopyableHex hex={c.hex} />
              </HUDFrame>
            ))}
          </div>
        </div>

        {/* Typography */}
        <div className="mt-16">
          <div className="hud-pill">TYPOGRAPHY</div>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {FONTS.map((f) => (
              <HUDFrame key={f.name} padding="p-6">
                <div
                  className="text-3xl text-white"
                  style={{ fontFamily: f.name, fontWeight: 700 }}
                >
                  {f.name}
                </div>
                <div className="mt-2 text-[color:var(--ss-green)] text-xs tracking-[0.22em] font-display">
                  {f.role.toUpperCase()}
                </div>
                <div className="text-xs text-[color:var(--ss-muted)] mt-1">
                  Weights: {f.weight}
                </div>
                <div
                  className="mt-4 text-[color:var(--ss-text)]"
                  style={{ fontFamily: f.name }}
                >
                  AaBbCc 0123456789
                </div>
              </HUDFrame>
            ))}
          </div>
        </div>

        {/* Boilerplate */}
        <div className="mt-16">
          <div className="hud-pill">COMPANY BOILERPLATE</div>
          <HUDFrame padding="p-6 md:p-8" className="mt-6">
            <p className="text-[color:var(--ss-text)] leading-relaxed">
              StrainScan Pro is an enterprise-grade, AI-powered agricultural
              intelligence platform built for operators across food and
              agriculture. Powered by Medical Care Technologies Inc.
              (OTC TICKER: MDCE), StrainScan Pro pairs computer vision with
              domain-specific machine learning models to deliver instant
              product recognition, lab-grade composition estimates, quality
              assessments, and end-to-end batch tracking.
            </p>
          </HUDFrame>
        </div>
      </div>
    </div>
  );
}
